import { useCallback, useEffect, useMemo, useState } from 'react';
import { authFetch } from '../../../utils/authFetch';
import { isDeadSessionStatus, recoverAdminSession } from '../../../utils/sessionRecovery';
import {
  WEEKDAYS,
  emptyWeek,
  weekFromPattern,
  patternFromWeek,
  validateWeek,
  affectsAvailability,
  isTerminal,
  explainLeave,
  isUpcomingOrCurrent,
  validateLeaveDates,
  validatePartialDay,
  describeLeaveWhen,
  isPartialDay,
  elapsedSince,
  formatMs,
  type WeekDraft,
  type Interval,
  type LeaveDto,
  type LeaveKind,
} from './myAvailabilityModel';

/**
 * Workspace Settings → My Work Availability.
 *
 * The member-facing half of Central's Availability domain. Everything here
 * already existed as an API; until now only an administrator could reach the
 * write side, which is why most members had no schedule and the resolver could
 * rarely say anything about them.
 *
 * THIS IS NOT A SECOND EDITOR. The sibling "Availability" tab is the
 * ADMINISTRATOR surface: workspace policy plus any member's hours. This tab
 * targets exactly one membership — the signed-in member's own — through the
 * same endpoints. There is one model, one validator and one set of rules;
 * the difference is only whose membershipId is in the URL.
 *
 * NOR IS IT APPOINTMENT SCHEDULING. "Work availability" is when a person
 * expects to work. Customer/appointment scheduling is a different bounded
 * context and deliberately shares nothing with this screen but a noun.
 *
 * TRUTHFULNESS IS THE POINT. A requested leave does not affect availability —
 * Central's engine blocks on `approved`/`active` only. This screen says so in
 * as many words on every requested leave, because the alternative is a member
 * who believes they are off, is not, and is routed work anyway.
 */

interface MyAvailabilityTabProps {
  workspaceId: string;
  authToken: string;
}

type Phase = 'loading' | 'ready' | 'disabled' | 'forbidden' | 'no-membership' | 'error';

interface AttendanceBreak { id: string; kind: string; startedAt: string; endedAt: string | null }
interface AttendanceSession {
  id: string;
  workLocation: string;
  clockInAt: string;
  clockOutAt: string | null;
  breaks: AttendanceBreak[];
}
interface HistoryItem extends AttendanceSession {
  durations: { totalSessionMs: number; totalBreakMs: number; netWorkedMs: number; sessionComplete: boolean } | null;
}

const LEAVE_KINDS: ReadonlyArray<{ value: LeaveKind; label: string }> = [
  { value: 'pto', label: 'Paid time off' },
  { value: 'vacation', label: 'Vacation' },
  { value: 'sick', label: 'Sick' },
  { value: 'other', label: 'Other' },
];

function todayIsoIn(timezone: string): string {
  try {
    return new Intl.DateTimeFormat('en-CA', { timeZone: timezone }).format(new Date());
  } catch {
    return new Date().toISOString().slice(0, 10);
  }
}

export function MyAvailabilityTab({ workspaceId, authToken: authTokenProp }: MyAvailabilityTabProps) {
  const [phase, setPhase] = useState<Phase>('loading');
  const [membershipId, setMembershipId] = useState<string | null>(null);

  /**
   * A token minted by `recoverAdminSession` after a 401, which supersedes the
   * prop for the rest of this mount.
   *
   * It has to live here rather than being pushed back up: `authToken` is owned
   * by WorkspaceSettings, and recovery writes to localStorage, which is not
   * reactive — so without this the recovered token would be stored and then
   * never used, and every subsequent request on this tab would keep presenting
   * the dead one. Every fetch below reads `authToken`, not the prop.
   */
  const [recoveredToken, setRecoveredToken] = useState<string | null>(null);
  const authToken = recoveredToken ?? authTokenProp;

  const [week, setWeek] = useState<WeekDraft>(emptyWeek());
  const [timezone, setTimezone] = useState<string>('');
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [scheduleError, setScheduleError] = useState<string | null>(null);

  const [leaves, setLeaves] = useState<LeaveDto[]>([]);
  const [leaveDraft, setLeaveDraft] = useState({
    kind: 'pto' as LeaveKind, startDate: '', endDate: '', allDay: true, startTime: '09:00', endTime: '12:00',
  });
  const [leaveError, setLeaveError] = useState<string | null>(null);
  const [leaveBusy, setLeaveBusy] = useState(false);

  const [session, setSession] = useState<AttendanceSession | null>(null);
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [expectedReason, setExpectedReason] = useState<string | null>(null);

  // One tick per second drives the live duration. The VALUE is always derived
  // from the server's clockInAt, so a sleeping tab or a missed tick costs a
  // second of staleness, never a permanent drift.
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    if (!session || session.clockOutAt !== null) return;
    const id = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(id);
  }, [session]);

  const issues = useMemo(() => validateWeek(week), [week]);

  /**
   * Find the signed-in member's own membershipId.
   *
   * Every availability endpoint is membership-scoped, and nothing in the token
   * carries a membership id — so it is resolved here from two existing reads:
   * `/api/auth/me` for the user id, and the workspace roster for the membership
   * that belongs to it. Both are already readable by any active member, so this
   * needs no new endpoint and grants no new access.
   */
  const loadMembership = useCallback(async (): Promise<string | null> => {
    const [meRes, peopleRes] = await Promise.all([
      authFetch('/api/auth/me', authToken),
      authFetch(`/api/workspaces/${workspaceId}/people`, authToken),
    ]);
    if (!meRes.ok || !peopleRes.ok) return null;
    const me = await meRes.json();
    const people = await peopleRes.json();
    const myUserId = me?.id;
    if (!myUserId) return null;
    const mine = (people.members ?? []).find((m: { userId?: string }) => m.userId === myUserId);
    return mine?.membershipId ?? null;
  }, [workspaceId, authToken]);

  const loadAll = useCallback(
    async (mid: string) => {
      const q = `membershipId=${encodeURIComponent(mid)}`;
      const [cfgRes, leaveRes, sessionRes, histRes, expRes] = await Promise.all([
        authFetch(`/api/availability/config/${mid}`, authToken),
        authFetch(`/api/availability/leaves?${q}`, authToken),
        authFetch('/api/availability/attendance/current-session', authToken),
        authFetch('/api/availability/attendance/history?limit=10&order=desc', authToken),
        authFetch(`/api/availability/expected?${q}`, authToken),
      ]);

      // 404 on config means "no configuration yet", which is a legitimate
      // starting state — an empty week, never an invented one.
      if (cfgRes.ok) {
        const body = await cfgRes.json();
        setWeek(weekFromPattern(body.config?.workingHours?.weekly));
        setTimezone(body.config?.timezone ?? '');
      } else {
        setWeek(emptyWeek());
      }
      if (leaveRes.ok) setLeaves(((await leaveRes.json()).leaves ?? []) as LeaveDto[]);
      if (sessionRes.ok) setSession((await sessionRes.json()).session ?? null);
      if (histRes.ok) setHistory((((await histRes.json()).sessions ?? []) as HistoryItem[]));
      if (expRes.ok) setExpectedReason((await expRes.json()).expected?.resolutionReason ?? null);
    },
    [authToken],
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // The feature gate answers 404 for the WHOLE domain when it is off.
        const probe = await authFetch('/api/availability/attendance/current-session', authToken);
        if (probe.status === 404) { if (!cancelled) setPhase('disabled'); return; }
        // 401 and 403 are NOT the same answer and must not share a branch.
        // They used to, and the result was that a dead session was reported as
        // "You do not have access to availability in this workspace" — an
        // access decision nobody made. A 401 means the credential is gone:
        // recover it, or hand the browser to Central sign-in. Only a 403 is a
        // genuine refusal, and only a 403 keeps the Access Denied notice.
        if (isDeadSessionStatus(probe.status)) {
          const refreshed = await recoverAdminSession();
          // Null means we are navigating to Central sign-in; stop touching state.
          if (!refreshed || cancelled) return;
          // Adopting the token changes `authToken`, which is in this effect's
          // dependency list, so the effect re-runs and this pass stands down.
          setRecoveredToken(refreshed);
          return;
        }
        if (probe.status === 403) { if (!cancelled) setPhase('forbidden'); return; }

        const mid = await loadMembership();
        if (cancelled) return;
        if (!mid) { setPhase('no-membership'); return; }
        setMembershipId(mid);
        await loadAll(mid);
        if (!cancelled) setPhase('ready');
      } catch {
        if (!cancelled) setPhase('error');
      }
    })();
    return () => { cancelled = true; };
  }, [authToken, loadMembership, loadAll]);

  // ── schedule ────────────────────────────────────────────────────────────────

  const setInterval_ = (weekday: number, index: number, patch: Partial<Interval>) => {
    setWeek((w) => {
      const next = { ...w, [weekday]: [...(w[weekday] ?? [])] };
      next[weekday]![index] = { ...next[weekday]![index]!, ...patch };
      return next;
    });
    setSavedAt(null);
  };
  const addInterval = (weekday: number) => {
    setWeek((w) => ({ ...w, [weekday]: [...(w[weekday] ?? []), { start: '09:00', end: '17:00' }] }));
    setSavedAt(null);
  };
  const removeInterval = (weekday: number, index: number) => {
    setWeek((w) => ({ ...w, [weekday]: (w[weekday] ?? []).filter((_, i) => i !== index) }));
    setSavedAt(null);
  };

  const saveSchedule = async () => {
    if (!membershipId || issues.length > 0) return;
    setSaving(true);
    setScheduleError(null);
    try {
      const res = await authFetch(`/api/availability/config/${membershipId}`, authToken, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ workingHours: { weekly: patternFromWeek(week) }, timezone }),
      });
      if (res.ok) {
        setSavedAt(new Date().toLocaleTimeString());
      } else {
        const body = await res.json().catch(() => null);
        // The server is the authority. Show exactly what it refused.
        setScheduleError(body?.error ?? `Could not save (HTTP ${res.status}).`);
      }
    } catch {
      setScheduleError('Could not reach Central. Nothing was saved.');
    } finally {
      setSaving(false);
    }
  };

  // ── leave ───────────────────────────────────────────────────────────────────

  const createLeave = async () => {
    if (!membershipId) return;
    const problem =
      validateLeaveDates(leaveDraft.startDate, leaveDraft.endDate) ??
      validatePartialDay(leaveDraft.startDate, leaveDraft.endDate, leaveDraft.allDay, leaveDraft.startTime, leaveDraft.endTime);
    if (problem) { setLeaveError(problem); return; }
    setLeaveBusy(true);
    setLeaveError(null);
    try {
      // `state` is deliberately NOT sent. A member cannot self-approve, and
      // asking would be a 403 — so the request carries only what it may.
      const res = await authFetch('/api/availability/leaves', authToken, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          membershipId,
          kind: leaveDraft.kind,
          startDate: leaveDraft.startDate,
          endDate: leaveDraft.endDate,
          // Omitted entirely for an all-day request. Sending "00:00"-"23:59"
          // would look identical in the database to a member who deliberately
          // chose those times, and whole-day leave is meant to carry no times
          // at all.
          ...(leaveDraft.allDay ? {} : { startTime: leaveDraft.startTime, endTime: leaveDraft.endTime }),
        }),
      });
      if (res.ok) {
        setLeaveDraft({ kind: 'pto', startDate: '', endDate: '', allDay: true, startTime: '09:00', endTime: '12:00' });
        await loadAll(membershipId);
      } else {
        const body = await res.json().catch(() => null);
        setLeaveError(body?.error ?? `Could not record this leave (HTTP ${res.status}).`);
      }
    } catch {
      setLeaveError('Could not reach Central. Nothing was recorded.');
    } finally {
      setLeaveBusy(false);
    }
  };

  const cancelLeave = async (leaveId: string) => {
    if (!membershipId) return;
    setLeaveBusy(true);
    setLeaveError(null);
    try {
      const res = await authFetch(`/api/availability/leaves/${leaveId}/cancel`, authToken, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        setLeaveError(body?.error ?? `Could not cancel this leave (HTTP ${res.status}).`);
      }
      await loadAll(membershipId);
    } finally {
      setLeaveBusy(false);
    }
  };

  // ── render ──────────────────────────────────────────────────────────────────

  if (phase === 'loading') return <p className="text-gray-500">Loading your availability…</p>;
  if (phase === 'disabled') return <Notice title="Availability is not enabled for this workspace." />;
  if (phase === 'forbidden') return <Notice title="You do not have access to availability in this workspace." />;
  if (phase === 'no-membership') return <Notice title="We could not find your membership in this workspace." />;
  if (phase === 'error') return <Notice title="Something went wrong loading your availability." />;

  const openSession = session && session.clockOutAt === null ? session : null;
  const openBreak = openSession?.breaks.find((b) => b.endedAt === null) ?? null;
  const elapsed = openSession ? elapsedSince(openBreak ? openBreak.startedAt : openSession.clockInAt, nowMs) : null;
  const today = todayIsoIn(timezone || 'UTC');
  const upcoming = leaves.filter((l) => isUpcomingOrCurrent(l, today));
  const past = leaves.filter((l) => !isUpcomingOrCurrent(l, today));

  return (
    <div className="space-y-8 max-w-3xl">
      {/* ── A. Current status ── */}
      <section aria-labelledby="my-avail-status">
        <h3 id="my-avail-status" className="text-lg font-semibold text-gray-900">Right now</h3>
        <div className="mt-3 rounded-lg border border-gray-200 p-4 flex flex-wrap items-center gap-x-6 gap-y-2">
          <div className="flex items-center gap-2">
            <span
              className={`inline-block w-2.5 h-2.5 rounded-full ${
                openBreak ? 'bg-amber-500' : openSession ? 'bg-green-500' : 'bg-gray-300'
              }`}
              aria-hidden="true"
            />
            <span className="font-medium text-gray-900" data-testid="my-avail-clock-state">
              {openBreak ? 'On break' : openSession ? 'Clocked in' : 'Clocked out'}
            </span>
          </div>
          {elapsed && (
            <span className="text-sm text-gray-600 tabular-nums" data-testid="my-avail-elapsed" aria-hidden="true">
              {elapsed}
            </span>
          )}
          {expectedReason && (
            <span className="text-sm text-gray-500" data-testid="my-avail-reason">
              Schedule says: {humanReason(expectedReason)}
            </span>
          )}
        </div>
      </section>

      {/* ── B. Work schedule ── */}
      <section aria-labelledby="my-avail-schedule">
        <h3 id="my-avail-schedule" className="text-lg font-semibold text-gray-900">My work schedule</h3>
        <p className="mt-1 text-sm text-gray-600">
          When you normally expect to work. A day with no periods is a day off. Overnight periods
          (for example 22:00–06:00) are not supported — split them across two days.
        </p>

        <label className="mt-4 block">
          <span className="text-sm font-medium text-gray-700">Timezone</span>
          <input
            type="text"
            value={timezone}
            onChange={(e) => { setTimezone(e.target.value); setSavedAt(null); }}
            placeholder="e.g. America/New_York"
            className="mt-1 block w-full max-w-xs rounded-md border-gray-300 shadow-sm text-sm"
            data-testid="my-avail-timezone"
          />
          <span className="mt-1 block text-xs text-gray-500">
            Your hours are read in this timezone, so they stay correct across daylight-saving changes.
          </span>
        </label>

        <div className="mt-4 space-y-3">
          {WEEKDAYS.map(({ weekday, label }) => {
            const intervals = week[weekday] ?? [];
            return (
              <div key={weekday} className="rounded-lg border border-gray-200 p-3" data-testid={`my-avail-day-${weekday}`}>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <span className="font-medium text-gray-900 text-sm">{label}</span>
                  <button
                    type="button"
                    onClick={() => addInterval(weekday)}
                    className="text-sm text-emerald-600 hover:text-emerald-800 min-h-[44px] px-2"
                    data-testid={`my-avail-add-${weekday}`}
                  >
                    + Add period
                  </button>
                </div>
                {intervals.length === 0 && <p className="mt-1 text-sm text-gray-500">Not working</p>}
                {intervals.map((iv, index) => {
                  const issue = issues.find((i) => i.weekday === weekday && i.index === index);
                  const errId = `err-${weekday}-${index}`;
                  return (
                    <div key={index} className="mt-2 flex flex-wrap items-center gap-2">
                      <label className="sr-only" htmlFor={`start-${weekday}-${index}`}>{label} period {index + 1} start</label>
                      <input
                        id={`start-${weekday}-${index}`}
                        type="time"
                        value={iv.start}
                        onChange={(e) => setInterval_(weekday, index, { start: e.target.value })}
                        aria-invalid={issue ? true : undefined}
                        aria-describedby={issue ? errId : undefined}
                        className="rounded-md border-gray-300 text-sm min-h-[44px]"
                      />
                      <span aria-hidden="true" className="text-gray-400">–</span>
                      <label className="sr-only" htmlFor={`end-${weekday}-${index}`}>{label} period {index + 1} end</label>
                      <input
                        id={`end-${weekday}-${index}`}
                        type="time"
                        value={iv.end}
                        onChange={(e) => setInterval_(weekday, index, { end: e.target.value })}
                        aria-invalid={issue ? true : undefined}
                        aria-describedby={issue ? errId : undefined}
                        className="rounded-md border-gray-300 text-sm min-h-[44px]"
                      />
                      <button
                        type="button"
                        onClick={() => removeInterval(weekday, index)}
                        className="text-sm text-gray-500 hover:text-red-600 min-h-[44px] px-2"
                        aria-label={`Remove ${label} period ${index + 1}`}
                      >
                        Remove
                      </button>
                      {issue && (
                        <p id={errId} role="alert" className="w-full text-sm text-red-600">{issue.message}</p>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>

        {scheduleError && <p role="alert" className="mt-3 text-sm text-red-600" data-testid="my-avail-schedule-error">{scheduleError}</p>}
        <div className="mt-4 flex items-center gap-3 flex-wrap">
          <button
            type="button"
            onClick={saveSchedule}
            disabled={saving || issues.length > 0}
            className="rounded-md bg-emerald-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-50 min-h-[44px]"
            data-testid="my-avail-save-schedule"
          >
            {saving ? 'Saving…' : 'Save schedule'}
          </button>
          {issues.length > 0 && (
            <span className="text-sm text-red-600">Fix {issues.length} problem{issues.length === 1 ? '' : 's'} above first.</span>
          )}
          {savedAt && <span className="text-sm text-green-700" data-testid="my-avail-saved">Saved at {savedAt}</span>}
        </div>
      </section>

      {/* ── C. Leave ── */}
      <section aria-labelledby="my-avail-leave">
        <h3 id="my-avail-leave" className="text-lg font-semibold text-gray-900">Leave and time off</h3>
        <p className="mt-1 text-sm text-gray-600">
          Record a whole day, several days, or a few hours. A request does not change your
          availability until an administrator approves it.
        </p>

        <div className="mt-4 rounded-lg border border-gray-200 p-4 space-y-3">
          <div className="flex flex-wrap gap-3">
            <label className="flex-1 min-w-[10rem]">
              <span className="text-sm font-medium text-gray-700">Type</span>
              <select
                value={leaveDraft.kind}
                onChange={(e) => setLeaveDraft((d) => ({ ...d, kind: e.target.value as LeaveKind }))}
                className="mt-1 block w-full rounded-md border-gray-300 text-sm min-h-[44px]"
                data-testid="my-avail-leave-kind"
              >
                {LEAVE_KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
              </select>
            </label>
            <label className="flex-1 min-w-[10rem]">
              <span className="text-sm font-medium text-gray-700">First day</span>
              <input
                type="date"
                value={leaveDraft.startDate}
                onChange={(e) => setLeaveDraft((d) => ({ ...d, startDate: e.target.value }))}
                className="mt-1 block w-full rounded-md border-gray-300 text-sm min-h-[44px]"
                data-testid="my-avail-leave-start"
              />
            </label>
            <label className="flex-1 min-w-[10rem]">
              <span className="text-sm font-medium text-gray-700">Last day</span>
              <input
                type="date"
                value={leaveDraft.endDate}
                onChange={(e) => setLeaveDraft((d) => ({ ...d, endDate: e.target.value }))}
                className="mt-1 block w-full rounded-md border-gray-300 text-sm min-h-[44px]"
                data-testid="my-avail-leave-end"
              />
            </label>
          </div>
          <fieldset className="border-0 p-0 m-0">
            <legend className="text-sm font-medium text-gray-700">When</legend>
            <div className="mt-1 flex flex-wrap gap-4">
              <label className="flex items-center gap-2 text-sm min-h-[44px]">
                <input
                  type="radio"
                  name="leave-span"
                  checked={leaveDraft.allDay}
                  onChange={() => setLeaveDraft((d) => ({ ...d, allDay: true }))}
                  data-testid="my-avail-leave-allday"
                />
                <span>All day</span>
              </label>
              <label className="flex items-center gap-2 text-sm min-h-[44px]">
                <input
                  type="radio"
                  name="leave-span"
                  checked={!leaveDraft.allDay}
                  onChange={() =>
                    setLeaveDraft((d) => ({
                      ...d,
                      allDay: false,
                      // A timed request is single-day. Pulling the end date to
                      // match avoids offering a combination the server refuses.
                      endDate: d.startDate || d.endDate,
                    }))
                  }
                  data-testid="my-avail-leave-timed"
                />
                <span>Specific times</span>
              </label>
            </div>
          </fieldset>

          {!leaveDraft.allDay && (
            <div className="flex flex-wrap gap-3" data-testid="my-avail-leave-times">
              <label className="flex-1 min-w-[8rem]">
                <span className="text-sm font-medium text-gray-700">From</span>
                <input
                  type="time"
                  value={leaveDraft.startTime}
                  onChange={(e) => setLeaveDraft((d) => ({ ...d, startTime: e.target.value }))}
                  className="mt-1 block w-full rounded-md border-gray-300 text-sm min-h-[44px]"
                  data-testid="my-avail-leave-start-time"
                />
              </label>
              <label className="flex-1 min-w-[8rem]">
                <span className="text-sm font-medium text-gray-700">To</span>
                <input
                  type="time"
                  value={leaveDraft.endTime}
                  onChange={(e) => setLeaveDraft((d) => ({ ...d, endTime: e.target.value }))}
                  className="mt-1 block w-full rounded-md border-gray-300 text-sm min-h-[44px]"
                  data-testid="my-avail-leave-end-time"
                />
              </label>
              <p className="w-full text-xs text-gray-500">
                Times are in {timezone || 'your workspace timezone'}. A timed request covers one day only.
              </p>
            </div>
          )}

          {leaveError && <p role="alert" className="text-sm text-red-600" data-testid="my-avail-leave-error">{leaveError}</p>}
          <button
            type="button"
            onClick={createLeave}
            disabled={leaveBusy}
            className="rounded-md bg-emerald-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-50 min-h-[44px]"
            data-testid="my-avail-add-leave"
          >
            Request leave
          </button>
        </div>

        <h4 className="mt-5 text-sm font-semibold text-gray-900">Upcoming and current</h4>
        {upcoming.length === 0 && <p className="mt-1 text-sm text-gray-500">No upcoming leave.</p>}
        <ul className="mt-2 space-y-2">
          {upcoming.map((l) => <LeaveRow key={l.leaveId} leave={l} busy={leaveBusy} onCancel={() => cancelLeave(l.leaveId)} />)}
        </ul>

        {past.length > 0 && (
          <>
            <h4 className="mt-5 text-sm font-semibold text-gray-900">Past and cancelled</h4>
            <ul className="mt-2 space-y-2">
              {past.map((l) => <LeaveRow key={l.leaveId} leave={l} busy={leaveBusy} onCancel={() => cancelLeave(l.leaveId)} />)}
            </ul>
          </>
        )}
      </section>

      {/* ── D. Attendance history ── */}
      <section aria-labelledby="my-avail-history">
        <h3 id="my-avail-history" className="text-lg font-semibold text-gray-900">Recent attendance</h3>
        {history.length === 0 && <p className="mt-1 text-sm text-gray-500">No recorded sessions yet.</p>}
        {history.length > 0 && (
          <div className="mt-3 overflow-x-auto">
            <table className="min-w-full text-sm" data-testid="my-avail-history">
              <thead>
                <tr className="text-left text-gray-500">
                  <th scope="col" className="py-2 pr-4 font-medium">Date</th>
                  <th scope="col" className="py-2 pr-4 font-medium">In</th>
                  <th scope="col" className="py-2 pr-4 font-medium">Out</th>
                  <th scope="col" className="py-2 pr-4 font-medium">Worked</th>
                </tr>
              </thead>
              <tbody>
                {history.map((h) => (
                  <tr key={h.id} className="border-t border-gray-100">
                    <td className="py-2 pr-4 whitespace-nowrap">{new Date(h.clockInAt).toLocaleDateString()}</td>
                    <td className="py-2 pr-4 whitespace-nowrap">{new Date(h.clockInAt).toLocaleTimeString()}</td>
                    <td className="py-2 pr-4 whitespace-nowrap">
                      {h.clockOutAt ? new Date(h.clockOutAt).toLocaleTimeString() : <span className="text-gray-400">still open</span>}
                    </td>
                    <td className="py-2 pr-4 whitespace-nowrap tabular-nums">
                      {h.durations ? formatMs(h.durations.netWorkedMs) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function Notice({ title }: { title: string }) {
  return <p className="text-gray-600" role="status">{title}</p>;
}

function LeaveRow({ leave, busy, onCancel }: { leave: LeaveDto; busy: boolean; onCancel: () => void }) {
  const explain = explainLeave(leave.state);
  const tone =
    explain.tone === 'active' ? 'bg-green-100 text-green-800'
    : explain.tone === 'pending' ? 'bg-amber-100 text-amber-800'
    : 'bg-gray-100 text-gray-600';
  return (
    <li className="rounded-lg border border-gray-200 p-3" data-testid={`my-avail-leave-${leave.leaveId}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <span className="font-medium text-gray-900" data-testid={`my-avail-leave-when-${leave.leaveId}`}>
            {describeLeaveWhen(leave)}
          </span>
          <span className="ml-2 text-sm text-gray-500 capitalize">{leave.kind}</span>
          {isPartialDay(leave) && (
            <span className="ml-2 rounded-full bg-emerald-50 px-2 py-0.5 text-xs text-emerald-700" data-testid={`my-avail-leave-partial-${leave.leaveId}`}>
              Part day
            </span>
          )}
        </div>
        <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${tone}`} data-testid={`my-avail-leave-badge-${leave.leaveId}`}>
          {explain.badge}
        </span>
      </div>
      {/* The sentence that keeps this screen honest. */}
      <p className="mt-1 text-sm text-gray-600" data-testid={`my-avail-leave-effect-${leave.leaveId}`}>
        {explain.effect}
      </p>
      {!isTerminal(leave.state) && (
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          className="mt-2 text-sm text-gray-500 hover:text-red-600 disabled:opacity-50 min-h-[44px]"
          data-testid={`my-avail-cancel-${leave.leaveId}`}
        >
          Cancel
        </button>
      )}
      {affectsAvailability(leave.state) && <span className="sr-only">This leave currently affects your availability.</span>}
    </li>
  );
}

/** Engine vocabulary → a sentence a member can act on. */
function humanReason(reason: string): string {
  switch (reason) {
    case 'approved_leave': return 'you are on approved leave';
    case 'holiday': return 'it is a holiday';
    case 'working_hours': return 'you are within your working hours';
    case 'default_off': return 'you are outside your working hours';
    default: return reason;
  }
}

export default MyAvailabilityTab;
